import React, { useMemo } from 'react';
import {
  gql,
  useLazyQuery,
  usePaginationHelper,
  useQuery,
} from '@common/hooks';
import {
  AppContent,
  buildOptionsFromData,
  DateWithoutTimeZone,
  ErrorHandler,
  FilteredTable,
  MultiSelectTableFilter,
  TextSearchTableFilter,
  TimeWithTimeZone,
} from '@common/components';
import { CandidateLink } from 'works/legacy/components/candidate-link';
import { ApplicationStateBadge } from 'works/legacy/components/application-state-badge';
import { RolesQuery } from '@clinician-profile/graphql';
import { titleize } from '@common/services';
import { ENUM_QUERY } from '@common/graphql';
import { handleEmploymentTypeFilter } from '@clinician-profile/services';

const CANDIDATES_QUERY = gql`
  query PaginatedStaffingRequestsApplicationsQuery(
    $limit: Int!
    $offset: Int!
    $orderDirection: OrderDirection
    $orderField: StaffingRequestApplicationOrderField
    $staffingRequestId: ID
    $filters: StaffingRequestApplicationFilterInput
  ) {
    paginatedStaffingRequestApplications(
      limit: $limit
      offset: $offset
      orderDirection: $orderDirection
      orderField: $orderField
      staffingRequestId: $staffingRequestId
      filters: $filters
    ) {
      staffingRequestApplications {
        id
        state
        createdAt
        stateChangedAt
        rejectionReason
        employmentType
        activeOffer {
          id
          state
          stateChangedAt
        }
        worksProfile {
          id
          firstName
          lastName
        }
        staffingRequest {
          id
          externalId
          startDate
          hiringLeader {
            id
            name
          }
          role {
            id
            abbreviation
          }
          organizationBusiness {
            id
            name
            city
            stateCode
          }
          organizationUnit {
            id
            name
          }
        }
        supplier {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const WORKS_SUPPLIERS = gql`
  query WorksSuppliers {
    suppliers {
      id
      name
    }
  }
`;

const WORKS_ORGANIZATION_UNIT_QUERY = gql`
  query WorksOrganizationUnits {
    organizationUnits {
      id
      name
    }
  }
`;

const ORG_BUSINESS_QUERY = gql`
  query CandidatesListOrganizationBusinesses {
    organizationBusinesses {
      id
      name
      organization {
        id
        name
      }
    }
  }
`;

export function CandidatesList() {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'WorksFilters::CandidatesTable',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'STATUS',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'NAME',
      submitted: 'SUBMITTED',
      externalId: 'EXTERNAL_ID',
      supplier: 'SUPPLIER',
      organizationUnit: 'UNIT',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
      hiringLeader: 'MANAGER',
      role: 'ROLE',
      startDate: 'START',
      state: 'STATUS',
      employmentType: 'EMPLOYMENT_TYPE',
    },
  });

  const [fetchSuppliers, { error: supplierError, data: supplierData }] =
    useLazyQuery(WORKS_SUPPLIERS);

  const [fetchRoles, { error: rolesError, data: rolesData }] =
    useLazyQuery(RolesQuery);

  const [
    fetchOrganizationUnits,
    { error: organizationUnitError, data: organizationUnitData },
  ] = useLazyQuery(WORKS_ORGANIZATION_UNIT_QUERY);

  const [
    fetchOrganizationBusiness,
    { error: orgBusinesserror, data: orgBusinessData },
  ] = useLazyQuery(ORG_BUSINESS_QUERY);

  const [
    fetchEmploymentTypes,
    { error: employmentTypesError, data: employmentTypesData },
  ] = useLazyQuery(ENUM_QUERY, { variables: { key: 'EmploymentTypes' } });

  const { loading, error, data } = useQuery(CANDIDATES_QUERY, {
    variables: {
      ...paginationVariables,
      filters,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      fetchRoles();
      fetchSuppliers();
      fetchOrganizationUnits();
      fetchOrganizationBusiness();
      fetchEmploymentTypes();
    },
  });

  const filtersDataReady =
    !!rolesData &&
    !!supplierData &&
    !!organizationUnitData &&
    !!orgBusinessData &&
    !!employmentTypesData;

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        width: 380,
        sorter: true,
        sorterField: 'NAME',
        render: (application) => (
          <CandidateLink
            firstName={application.worksProfile.firstName}
            lastName={application.worksProfile.lastName}
            link={`/works/applications/${application.id}`}
          />
        ),
      },
      {
        title: 'Submitted',
        key: 'submitted',
        sorter: true,
        sorterField: 'SUBMITTED',
        render: (application) => (
          <TimeWithTimeZone date={application.createdAt} variant="mid_format" />
        ),
      },
      {
        title: 'Supplier',
        dataIndex: ['supplier', 'name'],
        key: 'supplier',
        sorter: true,
        sorterField: 'SUPPLIER',
        filtered: filters.supplier || false,
        filteredValue: filters.supplier || null,
        filters: buildOptionsFromData(supplierData?.suppliers),
        filterDropdown: MultiSelectTableFilter,
      },
      {
        title: 'External Job ID',
        dataIndex: ['staffingRequest', 'externalId'],
        key: 'externalId',
        sorter: true,
        sorterField: 'EXTERNAL_ID',
      },
      {
        title: 'Facility / City',
        dataIndex: 'organizationBusiness',
        key: 'organizationBusiness',
        filtered: filters.organizationBusiness || false,
        filteredValue: filters.organizationBusiness || null,
        filters: buildOptionsFromData(
          orgBusinessData?.organizationBusinesses,
          (organizationBusiness) => ({
            value: organizationBusiness.id,
            text: organizationBusiness.name,
          })
        ),
        filterDropdown: MultiSelectTableFilter,
        render: function RenderFacilityCityState(_, record) {
          let organizationBusiness =
            record.staffingRequest.organizationBusiness;
          return (
            <>
              {organizationBusiness.name}
              <br />
              <span className="secondary-paragraph">
                {organizationBusiness.city},{organizationBusiness.stateCode}
              </span>
            </>
          );
        },
        sorter: true,
        sorterField: 'ORGANIZATION_BUSINESS',
      },
      {
        title: 'Unit',
        key: 'organizationUnit',
        sorter: true,
        sorterField: 'UNIT',
        filtered: filters.organizationUnit || false,
        filteredValue: filters.organizationUnit || null,
        filters: buildOptionsFromData(organizationUnitData?.organizationUnits),
        filterDropdown: MultiSelectTableFilter,
        render: (application) => {
          const unitName = application.staffingRequest.organizationUnit.name;
          const businessName =
            application.staffingRequest.organizationBusiness.name;
          return (
            <>
              <div>{unitName}</div>
              <div className="secondary-paragraph">{businessName}</div>
            </>
          );
        },
      },
      {
        title: 'Manager',
        dataIndex: ['staffingRequest', 'hiringLeader', 'name'],
        key: 'hiringLeader',
        sorter: true,
        sorterField: 'MANAGER',
        filtered: filters.hiringLeader || false,
        filteredValue: filters.hiringLeader || null,
        filterDropdown: TextSearchTableFilter,
        className: 'sentry-mask',
      },
      {
        title: 'Start Date',
        dataIndex: ['staffingRequest', 'startDate'],
        key: 'startDate',
        sorter: true,
        sorterField: 'START',
        render: (startDate) => <DateWithoutTimeZone date={startDate} />,
      },
      {
        title: 'Role',
        dataIndex: ['staffingRequest', 'role', 'abbreviation'],
        key: 'role',
        sorter: true,
        sorterField: 'ROLE',
        filtered: filters.role || false,
        filteredValue: filters.role || null,
        filters: buildOptionsFromData(rolesData?.roles, (role) => ({
          text: role.abbreviation,
          value: role.id,
        })),
        filterDropdown: MultiSelectTableFilter,
      },
      {
        title: 'Employment',
        dataIndex: 'employmentType',
        key: 'employmentType',
        sorter: true,
        filtered: filters.employmentType || false,
        filteredValue: filters.employmentType || null,
        filters: handleEmploymentTypeFilter(employmentTypesData),
        filterDropdown: MultiSelectTableFilter,
        render: (_, record) => <>{titleize(record.employmentType)}</>,
      },
      {
        title: 'Status',
        key: 'state',
        sorter: true,
        sorterField: 'STATUS',
        filtered: filters.state || false,
        filteredValue: filters.state || null,
        filterDropdown: MultiSelectTableFilter,
        filters: [
          {
            text: 'Submitted',
            value: 'submitted',
          },
          {
            text: 'Change Requested',
            value: 'submittal_packet_change_requested',
          },
          {
            text: 'In Review',
            value: 'in_review',
          },
          {
            text: 'Offer',
            value: 'offered',
          },
          {
            text: 'Accepted',
            value: 'accepted',
          },
          {
            text: 'Withdrawn',
            value: 'withdrawn',
          },
          {
            text: 'Rejected',
            value: 'rejected',
          },
        ],
        render: (application) => {
          const offerState = application.activeOffer
            ? application.activeOffer.state
            : null;
          const offerStateChangedAt = application.activeOffer
            ? application.activeOffer.stateChangedAt
            : null;

          return (
            <ApplicationStateBadge
              state={application.state}
              reason={application.rejectionReasonDescription}
              rejectionDetails={application.rejectionDetails}
              timestamp={application.stateChangedAt}
              offerState={offerState}
              offerTimestamp={offerStateChangedAt}
            />
          );
        },
      },
    ],
    [
      filters.hiringLeader,
      filters.organizationBusiness,
      filters.organizationUnit,
      filters.role,
      filters.state,
      filters.supplier,
      filters.employmentType,
      orgBusinessData?.organizationBusinesses,
      organizationUnitData?.organizationUnits,
      rolesData?.roles,
      supplierData?.suppliers,
      employmentTypesData,
    ]
  );

  const anyError =
    error ||
    supplierError ||
    rolesError ||
    organizationUnitError ||
    orgBusinesserror ||
    employmentTypesError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="CandidatesList" />;
  }

  const records =
    !loading && data
      ? data.paginatedStaffingRequestApplications.staffingRequestApplications
      : [];

  const total =
    !loading && data ? data.paginatedStaffingRequestApplications.totalCount : 0;

  return (
    <AppContent right={false} left={false}>
      <FilteredTable
        rowKey="id"
        columns={columns}
        loading={loading}
        dataSource={records}
        filtersDataReady={filtersDataReady}
        pagination={{ ...paginationVariables, total }}
        localStorageFilterKey={localStorageFilterKey}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
      />
    </AppContent>
  );
}
